import type { ApiResponse, ApiError, ApiMeta } from '@meshsmith/shared';

const BASE = (import.meta.env.VITE_API_URL ?? '') + '/api/v1';

export const PREVIEW_PARAM = 'ms_preview';
const PREVIEW_STORAGE_KEY = 'ms_preview_token';

/** Capture a ?ms_preview=<token> link once and remember it for this browser. */
export function capturePreviewToken(): void {
  try {
    const url = new URL(window.location.href);
    const token = url.searchParams.get(PREVIEW_PARAM);
    if (token) {
      localStorage.setItem(PREVIEW_STORAGE_KEY, token);
      url.searchParams.delete(PREVIEW_PARAM);
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    }
  } catch {
    /* ignore */
  }
}

export function getPreviewToken(): string | null {
  try {
    return localStorage.getItem(PREVIEW_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearPreviewToken(): void {
  try {
    localStorage.removeItem(PREVIEW_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export class ApiRequestError extends Error {
  code: string;
  status: number;
  fields?: { path: string; message: string }[];
  constructor(err: ApiError['error'], status: number) {
    super(err.message);
    this.code = err.code;
    this.status = status;
    this.fields = err.fields;
  }
}

/** Fired when the server says the session is over (the refresh failed), so the page can stop showing someone as signed in. */
export const SESSION_ENDED_EVENT = 'ms:session-ended';

let refreshing: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (!refreshing) {
    refreshing = fetch(`${BASE}/auth/refresh`, { method: 'POST', credentials: 'include' })
      .then((r) => {
        if (!r.ok && r.status === 401) window.dispatchEvent(new Event(SESSION_ENDED_EVENT));
        return r.ok;
      })
      .catch(() => false)
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  retryOn401?: boolean;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = new URL(BASE + path, window.location.origin);
  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  }
  const preview = getPreviewToken();
  if (preview) url.searchParams.set(PREVIEW_PARAM, preview);

  const doFetch = () =>
    fetch(url.toString().replace(window.location.origin, ''), {
      method: opts.method ?? 'GET',
      credentials: 'include',
      headers: opts.body ? { 'Content-Type': 'application/json' } : undefined,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });

  let res = await doFetch();

  if (res.status === 401 && (opts.retryOn401 ?? true)) {
    const ok = await tryRefresh();
    if (ok) res = await doFetch();
  }

  const json = (await res.json().catch(() => null)) as ApiResponse<T> | null;
  if (!json) throw new ApiRequestError({ code: 'NETWORK', message: 'Network error' }, res.status);
  if (!json.ok) throw new ApiRequestError(json.error, res.status);
  return json.data;
}

/** Like apiFetch but also returns the envelope meta (pagination, requestId). */
export async function apiFetchWithMeta<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<{ data: T; meta: ApiMeta }> {
  const url = new URL(BASE + path, window.location.origin);
  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  }
  const preview = getPreviewToken();
  if (preview) url.searchParams.set(PREVIEW_PARAM, preview);
  let res = await fetch(url.toString().replace(window.location.origin, ''), {
    method: opts.method ?? 'GET',
    credentials: 'include',
    signal: opts.signal,
  });
  if (res.status === 401 && (opts.retryOn401 ?? true)) {
    if (await tryRefresh()) {
      res = await fetch(url.toString().replace(window.location.origin, ''), {
        method: opts.method ?? 'GET',
        credentials: 'include',
        signal: opts.signal,
      });
    }
  }
  const json = (await res.json().catch(() => null)) as ApiResponse<T> | null;
  if (!json) throw new ApiRequestError({ code: 'NETWORK', message: 'Network error' }, res.status);
  if (!json.ok) throw new ApiRequestError(json.error, res.status);
  return { data: json.data, meta: json.meta };
}

/** The file name a server suggests for a download (Content-Disposition), if it did. */
export function fileNameFrom(header: string | null): string | null {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header ?? '');
  return match ? decodeURIComponent(match[1]!.trim()) : null;
}

export interface DownloadResult {
  fileName: string;
  /** how many of the things asked for the server left out (orders with no invoice), when it says */
  skipped: number;
}

/** Authenticated file download (cookies + refresh), saved through the browser under the name the server gives it, or `fileName`. */
export async function apiDownload(path: string, fileName: string, query?: RequestOptions['query']): Promise<DownloadResult> {
  const url = new URL(BASE + path, window.location.origin);
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  }
  const target = url.toString().replace(window.location.origin, '');
  let res = await fetch(target, { credentials: 'include' });
  if (res.status === 401 && (await tryRefresh())) res = await fetch(target, { credentials: 'include' });
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as ApiError | null;
    throw new ApiRequestError(json?.error ?? { code: 'DOWNLOAD', message: 'Download failed' }, res.status);
  }
  const name = fileNameFrom(res.headers.get('Content-Disposition')) ?? fileName;
  const blobUrl = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  return { fileName: name, skipped: Number(res.headers.get('X-Documents-Skipped') ?? 0) || 0 };
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    apiFetch<T>(path, { query, signal }),
  getWithMeta: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    apiFetchWithMeta<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};
